import { execCmd } from "./editorCommands";
import { setzeListenStart } from "./listenStart";
import { absatzEinruecken, amZeilenanfang, einzugPt, absatzVon, tabEinfuegen, tabVorCursor } from "./tabulator";
import { stoppsVon } from "./tabAusrichtung";
import {
    erkenneListenMarker,
    findeListenpunkt,
    istListenpunktLeer,
    textVorCursorInZeile,
} from "./autoListe";
import { aktiveAuswahl, aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";
import { istDom } from "../../windowManager/browserFenster/domArt";

/**
 * Listen entstehen und enden beim Tippen (Word-/Outlook-Verhalten) — die
 * DOM-Arbeit zu den Regeln aus `autoListe.ts`, für JEDEN `contentEditable`
 * im Haus.
 *
 *  - Leertaste nach „-", „*", „1." oder „1)" am Zeilenanfang → Liste,
 *    der Marker verschwindet und die Nummerierung zählt selbst weiter.
 *    Eine andere Zahl ist die Startnummer: „2. " beginnt bei 2 (T-347).
 *  - Tab / Shift+Tab INNERHALB einer Liste → eine Ebene tiefer/höher.
 *    Außerhalb bleibt Tab, was es war (Sprung zum nächsten Feld) — außer der
 *    Editor schaltet `tabImText` ein (Mail-Editor, T-346, siehe `tabulator.ts`):
 *    Dann rückt Tab am Absatzanfang den Absatz ein und fügt sonst einen
 *    Tabulator ein. Die Notiz-Editoren bleiben bewusst ohne — dort wäre Tab
 *    außerhalb einer Liste eine Tastaturfalle.
 *  - Enter auf einem leeren Listenpunkt → Liste verlassen, statt einen
 *    weiteren leeren Punkt anzuhängen.
 *  - Rücktaste am ANFANG eines Listenpunkts → der Punkt verliert seine
 *    Nummer bzw. seinen Spiegelstrich und wird ein gewöhnlicher Absatz (in
 *    einer verschachtelten Liste: eine Ebene höher) — wie in Word und Google
 *    Docs. PO 28.09.2026: „1. konnte ich nicht löschen, musste die
 *    Aufzählung über Menü aufheben."
 *
 * ══ EIN REGELWERK, DREI EDITOREN ═══════════════════════════════════════════
 * Bis zum 14.09.2026 lag das als Closure im `RichTextEditor` des Mail-
 * Verfassens. Der PO wollte dieselben Aufzählungen „mit automatischer
 * Einrückung" auf der Termin-Notizen-Seite — und die V4-Notizblöcke hatten
 * Listen nur über das Rechtsklick-Menü, ohne Tab. Statt die Closure zweimal
 * abzuschreiben, steht sie jetzt hier; die drei Editoren rufen sie in ihrem
 * `onKeyDown` auf. Was WANN eine Liste ist, entscheidet weiterhin allein
 * `autoListe.ts` — dort ist es ohne DOM prüfbar.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Rückgabe `true` heißt „erledigt": der Aufrufer reicht das Ereignis dann
 * nicht mehr an den Browser weiter. `onInput` wird gerufen, wenn sich der
 * Inhalt geändert hat — die Editoren hängen dort ihr Speichern an, und ein
 * `execCommand` löst in keinem Browser verlässlich ein `input`-Ereignis aus.
 */
export function behandleListenTaste(
    root: HTMLElement | null,
    e: Pick<React.KeyboardEvent, "key" | "shiftKey" | "ctrlKey" | "metaKey" | "altKey" | "preventDefault">,
    onInput?: () => void,
    optionen: { tabImText?: boolean } = {},
): boolean {
    if (!root || e.ctrlKey || e.metaKey || e.altKey) return false;
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    if (!sel || sel.rangeCount === 0) return false;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer)) return false;

    // Über einer Markierung: Ohne `tabImText` passiert hier nichts (Marker
    // stehen dort ohnehin nicht). Mit: Tab rückt markierte Listenpunkte ein
    // bzw. ersetzt die Markierung durch einen Tabulator — wie in Outlook.
    if (!sel.isCollapsed) {
        if (!optionen.tabImText || e.key !== "Tab") return false;
        const inListe = findeListenpunkt(range.startContainer, root);
        if (inListe) {
            e.preventDefault();
            execCmd(root, e.shiftKey ? "outdent" : "indent");
            onInput?.();
            return true;
        }
        if (e.shiftKey) return false;
        e.preventDefault();
        tabEinfuegen(root);
        onInput?.();
        return true;
    }

    const li = findeListenpunkt(range.startContainer, root);

    if (e.key === "Tab" && li) {
        e.preventDefault();
        execCmd(root, e.shiftKey ? "outdent" : "indent");
        onInput?.();
        return true;
    }

    if (optionen.tabImText && !li && (e.key === "Tab" || (e.key === "Backspace" && !e.shiftKey))) {
        if (behandleTabImText(root, range, e)) {
            onInput?.();
            return true;
        }
        return false;
    }

    if (e.key === "Enter" && !e.shiftKey && li && istListenpunktLeer(li.textContent ?? "")) {
        // `outdent` statt des Browser-Defaults: bei einer verschachtelten
        // Liste geht es eine Ebene hoch, auf oberster Ebene wird der Punkt
        // wieder zu einem Absatz. Beides ist hier gewollt.
        e.preventDefault();
        execCmd(root, "outdent");
        onInput?.();
        return true;
    }

    if (e.key === "Backspace" && !e.shiftKey && li && amAnfangVon(li, range)) {
        e.preventDefault();
        execCmd(root, "outdent");
        onInput?.();
        return true;
    }

    // In einer Liste beginnt kein neuer Marker — dort ist „- " Text.
    if (e.key !== " " || li) return false;
    const container = range.startContainer;
    const eigener = (container.textContent ?? "").slice(0, range.startOffset);
    const marker = erkenneListenMarker(eigener);
    if (!marker) return false;
    const art = marker.art;
    // Steht in derselben Zeile noch etwas VOR diesem Textknoten (etwa fett
    // gesetzter Text), ist es kein Zeilenanfang — dann bleibt die Leertaste
    // eine Leertaste.
    if (textVorCursorInZeile(container, range.startOffset, root) !== eigener) return false;

    e.preventDefault();
    const markerText = aktivesDokument().createRange();
    markerText.setStart(container, 0);
    markerText.setEnd(container, range.startOffset);
    markerText.deleteContents();
    sel.removeAllRanges();
    const danach = aktivesDokument().createRange();
    // DIE ZEILE MUSS SICHTBAR BLEIBEN. War der Marker alles, was in ihr stand
    // („1." auf einer neuen Zeile), bleibt ein leerer Textknoten in einem
    // sonst leeren Absatz zurück — für Chromium eine Zeile ohne Höhe. Es setzt
    // die Liste dann um die NÄCHSTE sichtbare Zeile, im Notizfeld also um die
    // Notiz darunter: gemessen 28.09.2026, „1. Punkt" in der Schreibstelle
    // machte die Notiz der Kollegin zum Listenpunkt und schrieb hinein. Ein
    // `<br>` hält die Zeile offen, wie es der Browser beim Leerräumen selbst tut.
    const zeile = container.parentNode as HTMLElement | null;
    if (zeile && zeile !== root && (zeile.textContent ?? "") === "" && !zeile.querySelector("br, img")) {
        const br = document.createElement("br");
        zeile.appendChild(br);
        danach.setStartBefore(br);
    } else if (zeile === root && (container.textContent ?? "") === "") {
        const br = document.createElement("br");
        root.insertBefore(br, container.nextSibling);
        danach.setStartBefore(br);
    } else if ((container.textContent ?? "") === "" && zeileEndetNach(container)) {
        // Die Zeile direkt UNTER einer gerade verlassenen Liste (gleicher
        // Absatz: `<ol>…</ol>-`). Ein Cursor im leeren Textknoten rutscht für
        // Chromium ans Ende der Liste darüber — dann wandelte „- " die ganze
        // obere Liste um (gemessen 22.10.2026). Ein `<br>` hält die eigene
        // Zeile offen, der Cursor steht davor.
        let br = container.nextSibling;
        while (br && br.nodeType === 3 && (br.textContent ?? "") === "") br = br.nextSibling;
        if (br?.nodeName !== "BR") {
            br = document.createElement("br");
            container.parentNode!.insertBefore(br, container.nextSibling);
        }
        danach.setStartBefore(br);
    } else {
        danach.setStart(container, 0);
    }
    danach.collapse(true);
    sel.addRange(danach);
    execCmd(root, art === "ul" ? "insertUnorderedList" : "insertOrderedList");
    if (art === "ol" && marker.start > 1) startnummerSetzen(root, marker.start);
    onInput?.();
    return true;
}

/**
 * „2. " → die eben angelegte Nummerierung beginnt bei 2 (T-347).
 *
 * Gesetzt wird nur, wenn der Punkt der ERSTE seiner Liste ist: Hängt der
 * Browser die neue Zeile an eine direkt darüberstehende Nummerierung an, zählt
 * die einfach weiter — ein `start` dort würde die ganze obere Liste umnummerieren.
 */
function startnummerSetzen(root: HTMLElement, start: number): void {
    const sel = aktiveAuswahl();
    const anker = sel && sel.rangeCount ? sel.getRangeAt(0).startContainer : null;
    const li = findeListenpunkt(anker, root);
    const ol = li?.parentElement;
    if (!li || !ol || ol.tagName !== "OL") return;
    if (ol.firstElementChild !== li) return;
    setzeListenStart(ol, start);
}

/**
 * Tab, Umschalt+Tab und Rücktaste außerhalb einer Liste (nur mit `tabImText`).
 * Rückgabe `true` = erledigt (Ereignis ist abgefangen).
 */
function behandleTabImText(
    root: HTMLElement,
    range: Range,
    e: Pick<React.KeyboardEvent, "key" | "shiftKey" | "preventDefault">,
): boolean {
    const anfang = amZeilenanfang(root, range);
    const absatz = absatzVon(root, range.startContainer);
    const eingerueckt = istDom(absatz, "HTMLElement") && einzugPt(absatz) > 0;

    if (e.key === "Backspace") {
        // Wie in Word: Rücktaste am Anfang eines eingerückten Absatzes nimmt
        // erst den Einzug zurück, statt die Zeile mit der vorigen zu verbinden.
        if (!anfang || !eingerueckt) return false;
        e.preventDefault();
        absatzEinruecken(root, -1);
        return true;
    }

    if (e.shiftKey) {
        if (anfang && eingerueckt) {
            e.preventDefault();
            absatzEinruecken(root, -1);
            return true;
        }
        if (tabVorCursor(range)) {
            e.preventDefault();
            execCmd(root, "delete");
            return true;
        }
        // Nichts zurückzunehmen: Umschalt+Tab bleibt der Weg aus dem Feld.
        return false;
    }

    e.preventDefault();
    // Mit eigenen Tabstopps springt Tab auch am Zeilenanfang auf den ersten
    // Stopp — dafür hat man ihn gesetzt. Ohne rückt Tab den Absatz ein.
    if (anfang && absatz !== null && stoppsVon(absatz).length === 0 && absatzEinruecken(root, 1)) return true;
    tabEinfuegen(root);
    return true;
}

/** Endet die Zeile hinter `n` — kommt danach nur ein `<br>`, ein Block oder nichts mehr? */
function zeileEndetNach(n: Node): boolean {
    let s = n.nextSibling;
    // Leere Textknoten (Reste des Browsers) zählen nicht als Inhalt.
    while (s && s.nodeType === 3 && (s.textContent ?? "") === "") s = s.nextSibling;
    if (!s) return true;
    if (s.nodeName === "BR") return true;
    return s.nodeType === 1 && ["DIV", "P", "UL", "OL", "TABLE", "BLOCKQUOTE"].includes(s.nodeName);
}

/**
 * Steht die Einfügemarke ganz vorn im Listenpunkt — vor jedem Zeichen und vor
 * jedem Bild? Eine verschachtelte Unterliste zählt nicht mit: sie steht HINTER
 * dem eigenen Text des Punkts.
 */
function amAnfangVon(li: HTMLElement, marke: Range): boolean {
    const davor = aktivesDokument().createRange();
    davor.selectNodeContents(li);
    try {
        davor.setEnd(marke.startContainer, marke.startOffset);
    } catch {
        return false;
    }
    if (davor.toString() !== "") return false;
    const inhalt = davor.cloneContents();
    return !inhalt.querySelector?.("img");
}
